import { useEffect } from 'react';
import {
	NavigationType,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { appScrollToTop } from '../lib/appScroll';

/**
 * Reset the scroll position on route navigation (PD-14672).
 *
 * React Router preserves the scroll offset across navigations — so
 * tapping into a detail page from a scrolled list (invoice line → item,
 * order → item, catalog deep in the grid) opened the new page already
 * scrolled down, with the back link pinned to the top edge. Every route
 * change lands at the top instead.
 *
 * The reset goes through `appScrollToTop` because the scroll offset
 * lives on the window on desktop but on `document.body` in the iOS
 * WKWebView (see `lib/appScroll.ts`) — the original window-only call was
 * a silent no-op in the native app.
 *
 * Keyed on `location.key` (not just pathname) so same-path navigations —
 * e.g. product → related product — also reset. Back/forward (POP) keeps
 * the browser's own restoration so returning to a long list doesn't lose
 * the user's place.
 */
export function ScrollToTop(): null {
	const location = useLocation();
	const navigationType = useNavigationType();

	useEffect(() => {
		if (navigationType === NavigationType.Pop) {
			return;
		}
		// 'auto' (not smooth) — an instant jump reads as a fresh page, while
		// animated scrolling on navigation looks like a glitch.
		appScrollToTop('auto');
	}, [location.key, navigationType]);

	return null;
}
